import React, { useEffect, useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faCheck, faClose, faPenToSquare, faSitemap, faTrash } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { useChecklist } from "../../../hooks/Checklist/useChecklist"
import type { ChecklistSector } from "../../../Objects/Checklist/ChecklistSector"
import { useConfirmation } from "../../../context/useConfirmationContext"

interface Props {
  onClose: () => void
  onChanged: () => void
}

const emptyForm = { name: "", colorHex: "#f97316" }

export const ChecklistSectorsPopup: React.FC<Props> = ({ onClose, onChanged }) => {
  const { t } = useTranslation(["pages", "general", "helpPopup"])

  const { getSectors, saveSector, deleteSector, isLoading } = useChecklist()

  const { setNewConfirmation } = useConfirmation()

  const [sectors, setSectors] = useState<ChecklistSector[]>([])

  const [newForm, setNewForm] = useState(emptyForm)

  const [editingId, setEditingId] = useState<number | null>(null)

  const [editForm, setEditForm] = useState(emptyForm)

  const load = async () => {
    const result = await getSectors()

    if (result) setSectors(result)
  }

  useEffect(() => {
    void load()
  }, [])

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()

    const result = await saveSector({
      id: null,
      name: newForm.name,
      colorHex: newForm.colorHex,
      order: sectors.length,
    })

    if (result) {
      setNewForm(emptyForm)

      await load()

      onChanged()
    }
  }

  const startEdit = (sector: ChecklistSector) => {
    setEditingId(sector.id)

    setEditForm({ name: sector.name, colorHex: sector.colorHex ?? "#f97316" })
  }

  const cancelEdit = () => setEditingId(null)

  const saveEdit = async (sector: ChecklistSector) => {
    const result = await saveSector({
      id: sector.id,
      name: editForm.name,
      colorHex: editForm.colorHex,
      order: sector.order,
    })

    if (result) {
      setEditingId(null)

      await load()

      onChanged()
    }
  }

  const handleDelete = (sector: ChecklistSector) =>
    setNewConfirmation(
      t("checklists.sectors.confirmDeleteTitle"),
      t("checklists.sectors.confirmDeleteMessage", { name: sector.name }),
      () => {
        void (async () => {
          const result = await deleteSector(sector.id)

          if (result) {
            await load()

            onChanged()
          }
        })()
      },
    )

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("checklists.sectors.title")}
              icon={faSitemap}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.checklist-sectors"
                    title={t("helpPopup:global.checklistSectors.title")}
                    description={t("helpPopup:global.checklistSectors.description")}
                    bullets={t("helpPopup:global.checklistSectors.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.button.onClose"
                  />
                </div>
              }
            />

            <form onSubmit={handleCreate} className="p-2">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <InputField
                    type="text"
                    icon={faSitemap}
                    value={newForm.name}
                    placeholder={t("checklists.sectors.namePlaceholder")}
                    onInvalidMessage={t("checklists.sectors.namePlaceholder")}
                    onChange={(e) => setNewForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    maxLength={100}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.input.name"
                  />
                </div>

                <input
                  type="color"
                  value={newForm.colorHex}
                  onChange={(e) => setNewForm((prev) => ({ ...prev, colorHex: e.target.value }))}
                  className="w-11 h-9 rounded border border-gray-300 shrink-0"
                  aria-label={t("checklists.sectors.colorLabel")}
                />
              </div>

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  label={t("general:label_add")}
                  typeButton="submit"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  disabled={isLoading}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.button.save"
                />
              </div>
            </form>

            <div className={defaultStyle.divisionBar}></div>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="flex flex-col gap-2 p-2 max-h-[40vh] overflow-y-auto">
                {sectors.map((sector) =>
                  editingId === sector.id ? (
                    <div
                      key={sector.id}
                      className="flex items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200"
                    >
                      <div className="flex-1">
                        <InputField
                          type="text"
                          value={editForm.name}
                          placeholder={t("checklists.sectors.namePlaceholder")}
                          onInvalidMessage={t("checklists.sectors.namePlaceholder")}
                          onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                          required
                          maxLength={100}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.input.editName"
                        />
                      </div>

                      <input
                        type="color"
                        value={editForm.colorHex}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, colorHex: e.target.value }))}
                        className="w-11 h-9 rounded-lg border border-gray-300 shrink-0"
                        aria-label={t("checklists.sectors.colorLabel")}
                      />

                      <ButtonIcon
                        sizeNumber={32}
                        bgColor={THEME.colors.buttonBGSaveColor}
                        icon={faCheck}
                        onClick={() => saveEdit(sector)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.button.confirmEdit"
                      />

                      <ButtonIcon
                        sizeNumber={32}
                        icon={faClose}
                        onClick={cancelEdit}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.button.cancelEdit"
                      />
                    </div>
                  ) : (
                    <div
                      key={sector.id}
                      className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 hover:bg-orange-50 border border-gray-200 transition-all"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="w-6 h-6 rounded-lg shrink-0 border border-gray-300"
                          style={{ backgroundColor: sector.colorHex ?? "#94a3b8" }}
                        />

                        <span className="font-semibold text-gray-800 truncate">{sector.name}</span>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <ButtonIcon
                          sizeNumber={32}
                          icon={faPenToSquare}
                          onClick={() => startEdit(sector)}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.button.edit"
                        />

                        <ButtonIcon
                          sizeNumber={32}
                          bgColor={THEME.colors.buttonBGRemoveColor}
                          icon={faTrash}
                          onClick={() => handleDelete(sector)}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistSectorsPopup.button.delete"
                        />
                      </div>
                    </div>
                  ),
                )}

                {sectors.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-6">{t("checklists.sectors.empty")}</p>
                )}
              </div>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
